import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom-v5-compat";
import { PACKAGES_QUERY_PARAM } from "@b2bportal/core-packages";
import type { AppDispatch } from "@b2bportal/core-types";
import { getPackageRoutes } from "@b2bportal/core-utilities";
import type { RestrictionCancel } from "@b2bportal/ground-api";
import { CAR_URL_PARAM_KEYS } from "@tenant/cars";
import { PackageCarsSelectors } from "../../store";
import { PackagesBaseActions } from "@/store/packages/slice/packages";

export const usePackageCarAvailabilityLink = (
  carId: string,
  cancellationPolicy?: RestrictionCancel
) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();

  const packageStateByVehicleId = useSelector(
    PackageCarsSelectors.getPackageStateByVehicleId
  );
  const newPackageState = packageStateByVehicleId?.[carId];

  const goToPackageShop = useCallback(() => {
    if (newPackageState) {
      dispatch(PackagesBaseActions.setCurrentActivePackage(newPackageState));
      const newParams = new URLSearchParams(params);
      newParams.set(CAR_URL_PARAM_KEYS.SHOP_ID, carId);
      newParams.set(
        PACKAGES_QUERY_PARAM.packageToken,
        newPackageState.token.value
      );

      if (cancellationPolicy) {
        dispatch(
          PackagesBaseActions.setSelectedCarCancellationPolicy(
            cancellationPolicy
          )
        );
      }

      navigate(
        `${getPackageRoutes().FULL_ROUTES.CARS.SHOP}?${newParams.toString()}`
      );
    } else {
      throw new Error("Car id not part of package state");
    }
  }, [carId, dispatch, navigate, newPackageState, params, cancellationPolicy]);

  return useMemo(() => {
    return {
      goToPackageShop,
    };
  }, [goToPackageShop]);
};
